import { useCallback, useEffect } from 'react'
import { styled } from '@mui/system'
import { get } from 'es-toolkit/compat'
import { FloatingPanel } from 'components/floating'
import {
  Mouse as MouseIcon,
  NotListedLocation as NotListedLocationIcon,
  QueryStats as QueryStatsIcon,
  Terrain as TerrainIcon,
} from '@mui/icons-material'
import { Button, ButtonGroup, buttonGroupClasses, Tooltip } from '@mui/material'
import { alpha, darken, lighten } from '@mui/material/styles'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { TEST_IDS } from 'test-ids'
import colors, { STATE_LAYER_OPACITY } from 'common/colors'
import { pickHoverLayer } from 'common/harvest-hover/pick-layer'

// Hover inspection downloads the layer through the harvest export endpoint,
// so it needs the same permissions that endpoint does.
const HOVER_INSPECTION_PERMISSIONS = ['harvest:write', 'farmtrx:admin']

export default function HarvestToolbar(props) {
  const {
    pointInfo,
    onPointInfo,
    swathInfo,
    onSwathInfo,
    hoverInfo,
    onHoverInfo,
    elevationProfile,
    onElevationProfile,
    // 3D mode: ol-cesium clears the map's interactions, so these tools cannot
    // work. Greyed out with an explanation rather than hidden, so the toolbar
    // keeps its shape and the reason is discoverable.
    disabled = false,
  } = props

  const { t } = useTranslation()

  const pointInfoEnabled = useSelector((state) =>
    get(state, 'map.pointInfoEnabled'),
  )
  const wmsLayers = useSelector((state) => get(state, 'map.wmsLayers', []))
  const swathLayerEnabled =
    wmsLayers &&
    wmsLayers.some((l) => l.name === 'swath' || l.layerName === 'swath')
  const harvestId = useSelector((state) => get(state, 'harvest.current.id'))
  const hoverLayerEnabled = pickHoverLayer(wmsLayers, harvestId) !== null
  const permissions = useSelector((state) =>
    get(state, 'account.permissions', []),
  )
  const canHoverInspect = HOVER_INSPECTION_PERMISSIONS.some((p) =>
    permissions.includes(p),
  )
  const demLayerEnabled =
    wmsLayers &&
    wmsLayers.some((l) => l.name === 'dem' || l.layerName === 'dem')

  useEffect(() => {
    if (!demLayerEnabled) {
      onElevationProfile(false)
    }
  }, [demLayerEnabled, onElevationProfile])

  useEffect(() => {
    if (!swathLayerEnabled) {
      onSwathInfo(false)
    }
  }, [swathLayerEnabled, onSwathInfo])

  useEffect(() => {
    if (!hoverLayerEnabled) {
      onHoverInfo(false)
    }
  }, [hoverLayerEnabled, onHoverInfo])

  let selectedBtn = ''
  if (pointInfo) selectedBtn = 'point-inspection'
  if (swathInfo) selectedBtn = 'swath-inspection'
  if (hoverInfo) selectedBtn = 'hover-inspection'
  if (elevationProfile) selectedBtn = 'elevation-profile'

  const toggleSelectedButton = useCallback(
    (e, id) => {
      if (selectedBtn === id) {
        if (id === 'point-inspection') {
          onPointInfo(false)
        } else if (id === 'swath-inspection') {
          onSwathInfo(false)
        } else if (id === 'hover-inspection') {
          onHoverInfo(false)
        } else if (id === 'elevation-profile') {
          onElevationProfile(false)
        }
      } else {
        if (selectedBtn === 'point-inspection') onPointInfo(false)
        if (selectedBtn === 'swath-inspection') onSwathInfo(false)
        if (selectedBtn === 'hover-inspection') onHoverInfo(false)
        if (selectedBtn === 'elevation-profile') onElevationProfile(false)

        if (id === 'point-inspection') {
          onPointInfo(true)
        } else if (id === 'swath-inspection') {
          onSwathInfo(true)
        } else if (id === 'hover-inspection') {
          onHoverInfo(true)
        } else if (id === 'elevation-profile') {
          onElevationProfile(true)
        }
      }
    },
    [selectedBtn, onPointInfo, onSwathInfo, onHoverInfo, onElevationProfile],
  )

  return (
    <FloatingPanel $position="top">
      <ButtonGroup
        id={TEST_IDS.harvestToolbar}
        data-testid={TEST_IDS.harvestToolbar}
        orientation="horizontal"
        aria-label="horizontal contained button group"
        variant="contained"
        sx={{
          [`.${buttonGroupClasses.grouped}:hover`]: {
            background: colors.greyHover,
          },
          [`.${buttonGroupClasses.disabled}:hover`]: {
            background: colors.greyHover,
          },
          [`.${buttonGroupClasses.disabled}:last-child`]: {
            borderTop: '0px solid gray !important',
          },
        }}
      >
        <Tooltip
          title={disabled ? t('tool_unavailable_in_3d') : t('point_inspection')}
          placement="top"
          disableInteractive
        >
          <span>
            <ToolbarButton
              id={TEST_IDS.pointInspection}
              data-testid={TEST_IDS.pointInspection}
              disabled={disabled || !pointInfoEnabled}
              selected={selectedBtn === 'point-inspection'}
              aria-label={t('point_inspection')}
              key="point-inspection"
              onClick={(e) => {
                toggleSelectedButton(e, 'point-inspection')
              }}
            >
              <NotListedLocationIcon
                titleAccess={t('point_inspection')}
                color={
                  selectedBtn === 'point-inspection' ? 'inherit' : 'primary'
                }
              />
              {selectedBtn === 'point-inspection' && (
                <FlyoutText>{t('point_inspection')}</FlyoutText>
              )}
            </ToolbarButton>
          </span>
        </Tooltip>
        <Tooltip
          title={disabled ? t('tool_unavailable_in_3d') : t('swath_inspection')}
          placement="top"
          disableInteractive
        >
          <span>
            <ToolbarButton
              id={TEST_IDS.swathInspection}
              data-testid={TEST_IDS.swathInspection}
              disabled={disabled || !pointInfoEnabled || !swathLayerEnabled}
              selected={selectedBtn === 'swath-inspection'}
              aria-label={t('swath_inspection')}
              key="swath-inspection"
              onClick={(e) => {
                toggleSelectedButton(e, 'swath-inspection')
              }}
            >
              <QueryStatsIcon
                titleAccess={t('swath_inspection')}
                color={
                  selectedBtn === 'swath-inspection' ? 'inherit' : 'primary'
                }
              />
              {selectedBtn === 'swath-inspection' && (
                <FlyoutText>{t('swath_inspection')}</FlyoutText>
              )}
            </ToolbarButton>
          </span>
        </Tooltip>
        {canHoverInspect ? (
          <Tooltip
            title={
              disabled ? t('tool_unavailable_in_3d') : t('hover_inspection')
            }
            placement="top"
            disableInteractive
          >
            <span>
              <ToolbarButton
                id={TEST_IDS.hoverInspection}
                data-testid={TEST_IDS.hoverInspection}
                disabled={disabled || !hoverLayerEnabled}
                selected={selectedBtn === 'hover-inspection'}
                aria-label={t('hover_inspection')}
                aria-pressed={selectedBtn === 'hover-inspection'}
                key="hover-inspection"
                onClick={(e) => {
                  toggleSelectedButton(e, 'hover-inspection')
                }}
              >
                <MouseIcon
                  titleAccess={t('hover_inspection')}
                  color={
                    selectedBtn === 'hover-inspection' ? 'inherit' : 'primary'
                  }
                />
                {selectedBtn === 'hover-inspection' && (
                  <FlyoutText>{t('hover_inspection')}</FlyoutText>
                )}
              </ToolbarButton>
            </span>
          </Tooltip>
        ) : null}
        <Tooltip
          title={
            disabled ? t('tool_unavailable_in_3d') : t('elevation_profile')
          }
          placement="top"
          disableInteractive
        >
          <span>
            <ToolbarButton
              id={TEST_IDS.elevationProfile}
              data-testid={TEST_IDS.elevationProfile}
              disabled={disabled || !demLayerEnabled}
              selected={selectedBtn === 'elevation-profile'}
              aria-label={t('elevation_profile')}
              key="elevation-profile"
              onClick={(e) => {
                toggleSelectedButton(e, 'elevation-profile')
              }}
            >
              <TerrainIcon
                titleAccess={t('elevation_profile')}
                color={
                  selectedBtn === 'elevation-profile' ? 'inherit' : 'primary'
                }
              />
              {selectedBtn === 'elevation-profile' && (
                <FlyoutText>{t('elevation_profile')}</FlyoutText>
              )}
            </ToolbarButton>
          </span>
        </Tooltip>
      </ButtonGroup>
    </FloatingPanel>
  )
}

const ToolbarButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ selected }) => ({
  justifyContent: 'center',
  background: selected ? colors.brand : colors.greyHover,
  color: selected ? colors.white : colors.brand,
  position: 'relative',
  overflow: 'visible',
  height: '40px',
  width: '40px',
  minWidth: '40px',
  padding: 0,
  '&:hover': {
    // Was previously identical to the resting background — no hover
    // feedback at all. Lighten the filled/selected button, darken the
    // plain one, each by M3's hover state-layer opacity.
    background: selected
      ? lighten(colors.brand, STATE_LAYER_OPACITY.hover)
      : darken(colors.greyHover, STATE_LAYER_OPACITY.hover),
  },
  '&:disabled': {
    background: alpha(colors.greyHover, 0.73),
    color: alpha(colors.brand, 0.73),
  },
  // If the Icon defaults to a certain size, adjust here if needed
  '& svg': {
    fontSize: '24px',
  },
}))

const FlyoutText = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 'calc(100% + 8px)',
  left: '50%',
  transform: 'translateX(-50%)',
  background: colors.brand,
  color: colors.white,
  padding: '6px 10px',
  borderRadius: '4px',
  whiteSpace: 'nowrap',
  fontSize: '0.875rem',
  pointerEvents: 'none',
  boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
  zIndex: 1400, // Make sure it's above maps and other items
  '&::before': {
    // small arrow pointing up to the button
    content: '""',
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    border: '6px solid transparent',
    borderBottomColor: colors.brand,
  },
}))
